import { FormikProps, FormikContextType } from "formik";
import ModelInterface from "../Interfaces/ModelInterface";
import ShapeInterface from "../Interfaces/ShapeInterface";
import DimensionRange from "../Interfaces/DimensionRange";
import { metaobject, sectionSettings, shapes } from "./WindowHelpers";
import VariantInterface from "../Interfaces/VariantInterface";
import PackageInterface from "../Interfaces/PackageInterface";
import ConfiguratorValue from "../Interfaces/ConfiguratorValue";
import { ConfiguratorDimensionValue, FormikDimensionValue } from "../Interfaces/ConfiguratorDimensionValue";

interface DimensionUpdateValue {
    dimension: string,
    value: number
}

const _dimensions = ['a', 'b', 'c', 'd'];

const _getModel = (model: string, brand: string, type: string) => {
    const raw = sessionStorage.getItem(`${type}_models`);
    const models: ModelInterface[] = raw ? JSON.parse(raw) : [];
    return brand
        ? (models.find(m => m._brand == brand && m._model == model) ?? null)
        : null
}

const _getDimensionRangeFromShape = (dimension: string, value: number, shape: ShapeInterface) => {
    const dimObj = shape.dimensions.find((dim) => dim.dimension == dimension);
    const ranges = (dimObj.ranges ?? []) as DimensionRange[];
    return getDimensionRange(ranges, value);
}

const getDimensionRange = (ranges: DimensionRange[], value: number) => {
    const range = ranges.filter((range) => parseFloat(range.high) >= value && parseFloat(range.low) <= value);
    if (range.length == 0) return null;
    return range.reduce((prev, current) => {
        if (prev.disable_continuation == 'true') return prev;
        if (current.disable_continuation == 'true') return current;
        if (current.price > prev.price) return current;
        return prev;
    })
}

const getShape = (shapeName: string) => {
    return shapes.find((item) => item.shape_id.toLowerCase() == shapeName.toLowerCase());
}

const getPanels = (shapeName: string) => getShape(shapeName)?.panels ?? [];

const getFoldDirections = (shapeName: string, panelName: string) => {
    return getPanels(shapeName)?.find((panel) => panel.number_of_panels == panelName)?.fold_directions ?? [];
}

const getStrapLocations = (shapeName: string, panelName: string, foldDirectionName: string) => {
    return getFoldDirections(shapeName, panelName)?.find((foldDirection) => foldDirection.option_select_name == foldDirectionName)?.strap_locations ?? [];
}

const _emptyDimension = (dimension: string, usesDimension: boolean, formik: FormikContextType<any> | FormikProps<any>) => {
    formik.setFieldValue(`dimension_${dimension}`, {
        value: null,
        variantId: '',
        price: '',
        message: '',
        disableContinuation: !usesDimension,
    });
    formik.setFieldTouched(`dimension_${dimension}.value`, false);
}

const clearValues = (names: string[], formik: FormikContextType<any> | FormikProps<any>) => {
    names.forEach((name) => {
        formik.setFieldValue(name, '');
        formik.setFieldTouched(name, false);
    })
}

const normalizeModelValue = (value?: string) => (value ?? '').toString().replaceAll('"', '').trim().toLowerCase();

const findOptionByName = <T extends { option_select_name: string }>(options: T[] = [], value?: string) => {
    const normalizedValue = normalizeModelValue(value);
    if (!normalizedValue) return null;
    return options.find((option) => normalizeModelValue(option.option_select_name) == normalizedValue) ?? null;
}

const updateModelFoldAndStrap = (modelObject: ModelInterface, shapeName: string, formik: FormikContextType<any> | FormikProps<any>) => {
    if (metaobject.rolling_covers) return;

    const shape = getShape(shapeName.replaceAll(' ', '_'));
    if (!shape || shape.custom == 'true') return;

    const modelFold = modelObject._fold_location;
    if (!modelFold) return;

    const matchingPanel = shape.panels.find((panel) => findOptionByName(panel.fold_directions, modelFold));
    if (!matchingPanel) return;

    formik.setFieldValue('panels', matchingPanel.number_of_panels);

    const matchingFold = findOptionByName(matchingPanel.fold_directions, modelFold);
    if (!matchingFold) return;

    formik.setFieldValue('fold_direction', matchingFold.option_select_name);

    const matchingStrap = findOptionByName(matchingFold.strap_locations, modelObject._strap_location);
    if (matchingStrap) {
        formik.setFieldValue('strap_location', matchingStrap.option_select_name);
    }
}

const updateModelObjectValues = (modelObject: ModelInterface | null, formik: FormikContextType<any> | FormikProps<any>) => {
    if (!modelObject) {
        formik.setFieldValue('model', '');
        return;
    }

    formik.setFieldValue('brand', modelObject._brand);
    formik.setFieldValue('model', modelObject._model);

    if (!modelObject._shape) return;

    const modelId = modelObject._shape + (metaobject.rolling_covers ? "_Rolling" : '');
    
    updateShapeValues(modelId, formik);
    updateModelFoldAndStrap(modelObject, modelId, formik);

    if (!formik.values.skirt_length?.variantId) {
        const skirtVariant = metaobject.skirt_length_options.find((item) => parseFloat(item.title) == parseFloat(modelObject._skirt_length || ''));
        if (skirtVariant) {
            formik.setFieldValue('skirt_length.variantId', skirtVariant.id);
            updateVariantObject('skirt_length', skirtVariant, formik, 3);
        }
    }

    const updateDimensionValues: DimensionUpdateValue[] = [];
    _dimensions.forEach((dimension) => {
        const dimensionValue = modelObject[`_dimension_${dimension}` as keyof ModelInterface];
        if (!dimensionValue) return;
        const dimensionFloat = parseFloat(dimensionValue);
        if (!dimensionFloat) return;
        updateDimensionValues.push({ dimension: dimension, value: dimensionFloat });
    })
    updateDimensions(updateDimensionValues, modelId, formik);
}

const updateModelValues = (model: string, brand: string, type: string, formik: FormikContextType<any> | FormikProps<any>) => {
    const modelObject = _getModel(model, brand, type);
    setModelUrl(modelObject);
    updateModelObjectValues(modelObject, formik);
}

const setModelUrl = (model: ModelInterface | null) => {
    const openButtons = document.querySelectorAll<HTMLButtonElement>('.configurator-redirect');
    openButtons.forEach((button) => button.dataset.url = sanitizedUrl(model?._url ?? ""));
}

const sanitizedUrl = (url: string) => {
		return url != null ? url : ''
	}

const updateShapeValues = (shapeName: string, formik: FormikContextType<any> | FormikProps<any>, clearBrandAndModel?: boolean) => {
    const sanitizedShapeName = shapeName.replaceAll(' ', '_');
    const shape = getShape(sanitizedShapeName);
    if (!shape) return;
    formik.setFieldValue('shape', sanitizedShapeName);
    formik.setFieldValue('customShape', shape.custom == 'true');
    cleanUpShapeControlledFields(shape, formik, clearBrandAndModel);

}

const cleanUpShapeControlledFields = (shape: ShapeInterface, formik: FormikContextType<any> | FormikProps<any>, clearBrandAndModel?: boolean) => {
    _dimensions.forEach((dimension) => {
        const dimensionUsed = shape[`dimension_${dimension}` as keyof ShapeInterface] == 'true';
        _emptyDimension(dimension, dimensionUsed, formik);
    })
    formik.setFieldTouched('dimensionsValid', false);

    formik.setFieldValue('dimensionsValid', false);

    if (metaobject.rolling_covers) {
        if (shape.panels.length == 1) {
            var panel = shape.panels[0];
            formik.setFieldValue('panels', panel.number_of_panels);
        }
        const foldDirection = shape.panels[0].fold_directions.find((foldDirection) => foldDirection.rolling_cover_default_direction?.toLowerCase() == 'true') ?? shape.panels[0].fold_directions[0];
        if (foldDirection) {
            formik.setFieldValue('fold_direction', foldDirection.option_select_name);
        }
        if (foldDirection.strap_locations[0]) {
            const strap = foldDirection.strap_locations[0];
            formik.setFieldValue('strap_location', strap.option_select_name);
        }
        return;
    }

    if (shape.custom == 'true') {
        formik.setFieldValue('attestation', true);
        formik.setFieldValue('fold_direction', 'custom');
        formik.setFieldValue('panels', 'custom');
        formik.setFieldValue('strap_location', 'custom');
        return;
    }

    // if the shape contains only one panel object, set it as default and don't show the panels dropdown

    const valuesToClear = ['fold_direction', 'strap_location'];
    if (clearBrandAndModel) {
        valuesToClear.push('brand');
        valuesToClear.push('model');
    }

    if (shape.panels && shape.panels.length > 1) {
        valuesToClear.push('panels');
        clearValues(valuesToClear, formik);
        return;
    }

    clearValues(valuesToClear, formik);
    var panel = shape.panels[0];
    formik.setFieldValue('panels', panel.number_of_panels);
    if (panel.fold_directions.length == 1) {
        var fold = panel.fold_directions[0];
        formik.setFieldValue('fold_direction', fold.option_select_name);
    }
}

const evaluateDimensionFormula = (formula: string, dimensions: DimensionUpdateValue[], formik: FormikContextType<any> | FormikProps<any>) => {
    if (!formula) return 0;
    ['a', 'b', 'c', 'd'].forEach((dim) => {
        const value = dimensions.find((dimension) => dimension.dimension == dim)?.value ?? formik.values[`dimension_${dim.toLowerCase()}`]?.value ?? 0;
        formula = formula.replace(dim.toUpperCase(), value);
    });

    return Math.round(eval(formula) * 100) / 100;
}

const updateIndividualDimension = (dimension: DimensionUpdateValue, ranges: DimensionRange[], formik: FormikContextType<any> | FormikProps<any>) => {
    console.log('updateIndividualDimension', dimension, ranges);
    const updatedDimensions: FormikDimensionValue[] = [];
    let newOversizeVariant = {
        value: '',
        variantId: '',
        price: ''
    };
    let disableContinuation = false;

    const fieldName = `dimension_${dimension.dimension}`;
    let newFormikValue = formik.values[fieldName] as ConfiguratorDimensionValue ??
        { dimension: dimension.dimension, value: 0, variantId: '', price: '', message: '', disableContinuation: false } as ConfiguratorDimensionValue;
    let newValue = dimension.value ?? formik.values[fieldName]?.value ?? 0;
    newFormikValue.value = newValue;

    updatedDimensions.push({
        dimension: dimension.dimension,
        value: newFormikValue
    });
    updatedDimensions.forEach((updateDimension) => {
        const range = getDimensionRange(ranges, updateDimension.value.value ?? 0);
        const dimensionDisableContinuation = range?.disable_continuation == "true";
        if (parseInt(range?.price || '0') > parseInt(newOversizeVariant.price || '0')) {
            newOversizeVariant = {
                value: 'Oversize Fee',
                variantId: range?.id,
                price: range?.price
            }
        }
        if (dimensionDisableContinuation) {
            disableContinuation = true;
        }
        const message = range?.message;
        updateDimension.value.message = message;
        updateDimension.value.disableContinuation = dimensionDisableContinuation;
        updateDimension.value.customSize = range?.custom_dimension ?? 'false';
        updateDimension.value.exemptions = range?.exemptions;
    });
    console.log('update Dimensions', updatedDimensions);
    updatedDimensions.forEach((dimension) => {
        formik.setFieldValue(`dimension_${dimension.dimension}`, dimension.value);
    });

    formik.setFieldValue('oversize_fee', newOversizeVariant);
}

const updateDimensions = (dimensions: DimensionUpdateValue[], shapeName: string, formik: FormikContextType<any> | FormikProps<any>) => {
    const sanitizedShapeName = shapeName.replaceAll(' ', '_');
    const shape = getShape(sanitizedShapeName);
    let disableContinuation = false;
    let newOversizeVariant = {
        value: '',
        variantId: '',
        price: ''
    };
    if (!shape) {
        console.error('updateDimension: Shape is Missing');
        return {};
    }

    const updatedDimensions: FormikDimensionValue[] = [];
    // first pass, update the formik values with the new dimensions
    shape.dimensions.forEach((shapeDimension) => {
        //if the dimension is being updated in this request, use that number otherwise, use the existing formik value
        const fieldName = `dimension_${shapeDimension.dimension}`;
        let newFormikValue = formik.values[fieldName] as ConfiguratorDimensionValue ??
            { dimension: shapeDimension.dimension, value: 0, variantId: '', price: '', message: '', disableContinuation: false } as ConfiguratorDimensionValue;
        let newValue = dimensions.find((dimension) => dimension.dimension == shapeDimension.dimension)?.value ?? formik.values[fieldName]?.value ?? 0;
        newFormikValue.value = newValue;
        if(shapeDimension.formula) {
            newFormikValue.formula = shapeDimension.formula;
        }
        updatedDimensions.push({
            dimension: shapeDimension.dimension,
            value: newFormikValue})
        });
    // second pass, run the formula if there is one
    updatedDimensions.forEach((updateDimension) => {
        if(!updateDimension.value.formula) return;
        const inputValue = evaluateDimensionFormula(updateDimension.value.formula, dimensions, formik);
        updateDimension.value.value = inputValue;
    });
    // third pass, validate dimensions against the shape ranges
    updatedDimensions.forEach((updateDimension) => {
        const shapeDimension = shape.dimensions.find((dim) => dim.dimension == updateDimension.dimension);
        const range = _getDimensionRangeFromShape(shapeDimension.dimension, updateDimension.value.value ?? 0, shape);
        const dimensionDisableContinuation = range?.disable_continuation == "true" || updateDimension.value.value == 0;
        if (parseInt(range?.price || '0') > parseInt(newOversizeVariant.price || '0')) {
            newOversizeVariant = {
                value: 'Oversize Fee',
                variantId: range?.id,
                price: range?.price
            }
        }
        if (dimensionDisableContinuation) {
            disableContinuation = true;
        }
        const message = updateDimension.value.value == 0 ? sectionSettings?.error_dimensions_is_zero : range?.message;
        updateDimension.value.message = message;
        updateDimension.value.disableContinuation = dimensionDisableContinuation;
        updateDimension.value.customSize = range?.custom_dimension ?? 'false';
        updateDimension.value.exemptions = range?.exemptions;
    });

    if (shape.dimension_a_enforce_longest == 'true') {
        const dimensionA = updatedDimensions.find((dim) => dim.dimension == 'a');
        updatedDimensions.forEach((dimension) => {
            if (dimension.dimension !== 'a') {
                if (dimension.value.value > dimensionA.value.value) {
                    disableContinuation = true;
                    dimension.value.message = sectionSettings.error_dimension_a_longest;
                }
            }
        });

    }
    updatedDimensions.forEach((dimension) => {
        formik.setFieldValue(`dimension_${dimension.dimension}`, dimension.value);
    });

    formik.setFieldValue('oversize_fee', newOversizeVariant);
    formik.setFieldValue('dimensionsValid', !disableContinuation);
    if (shape.rolling) {
        _updateFoldDirectionForRollingCovers(shape, updatedDimensions, formik);
    }
}

const _updateFoldDirectionForRollingCovers = (shape: ShapeInterface, dimensions: FormikDimensionValue[], formik: FormikContextType<any> | FormikProps<any>) => {

    if(shape.panels[0].fold_directions.length <= 1) {
        return;
    }
    const dimensionA = dimensions.find((dim) => dim.dimension == 'a');
    const dimensionB = dimensions.find((dim) => dim.dimension == 'b');
    if(!dimensionA || !dimensionB) return;
    let isALargest = dimensionA?.value?.value >= dimensionB?.value?.value;
    const foldDirection = shape.panels[0].fold_directions.find((fold) => 
        isALargest ? 
            fold.rolling_cover_default_direction?.toLowerCase() == "true" 
            : fold.rolling_cover_default_direction?.toLowerCase() != "true");
    if (!foldDirection) {
        console.error('updateDimension: Fold Direction is Missing');
        return;
    }
    formik.setFieldValue('fold_direction', foldDirection.option_select_name);
    const strap = foldDirection.strap_locations[0];
    formik.setFieldValue('strap_location', strap.option_select_name);
    
}

const updateVariantObject = (fieldName: string, variant: VariantInterface, formik: FormikContextType<any> | FormikProps<any>, step: number) => {
    if (!variant) {
        formik.setFieldValue(fieldName, {
            value: '',
            variantId: '',
            price: '',
            image: '',
            productId: '',
            step: 0,
        } as ConfiguratorValue);
        return;
    }
    formik.setFieldValue(fieldName, {
        value: variant.title,
        variantId: variant.id,
        price: variant.price,
        image: variant.featured_image?.src,
        productId: variant.product,
        step: step || 2
    } as ConfiguratorValue);
}

const changeSelectedVariant = (fieldName: string, variant: VariantInterface, formik: FormikContextType<any> | FormikProps<any>, step: number, altTitle?: string) => {
    const currentValues = (formik.values.variants as ConfiguratorValue[]) ?? [];
    // drop any existing entry for this productId, then add the new one
    const nextValues = currentValues.filter((item: any) => item.productId != variant.product);
    const newValue: ConfiguratorValue = {
        field: fieldName,
        value: altTitle ? altTitle : variant.title,
        price: variant.price,
        variantId: variant.id,
        image: variant.featured_image?.src,
        productId: variant.product,
        step: step
    };
    nextValues.push(newValue);
    formik.setFieldValue('variants', nextValues);
}

const updateUpsellPackages = (upsellPackage: PackageInterface, price: string, formik: FormikContextType<any> | FormikProps<any>, step: number) => {
    const currentValues = (formik.values.variants as ConfiguratorValue[]) ?? [];

    if (currentValues.some((item: ConfiguratorValue) => item.value == upsellPackage.name)) {

        const next = currentValues.filter((item: ConfiguratorValue) => item.value != upsellPackage.name);
        formik.setFieldValue('variants', next);  // new array reference
        return;
    }
    const associatedVariants = upsellPackage.variants.map((variant) => variant.id) ?? [];

    // Add immutably
    const next = [
        ...currentValues,
        {
            field: 'upsell_package',
            value: upsellPackage.name,
            price: price,
            associatedVariants: associatedVariants,
            step: step
        } as ConfiguratorValue,
    ];
    if (associatedVariants.length > 0) {
        formik.setFieldValue('variants', [...next.filter((item: any) => !associatedVariants.some((variantId) => variantId == item.variantId))]);
        return;
    }
    formik.setFieldValue('variants', next); // new array reference
}

const updateStringArray = (fieldName: string, value: string, formik: FormikContextType<any> | FormikProps<any>) => {
    const currentValues = (formik.values[fieldName] as string[]) ?? [];
    if (currentValues.some((val) => val == value.toLowerCase())) {
        const next = currentValues.filter((val) => val != value.toLowerCase());
        formik.setFieldValue(fieldName, next);
        return;
    }
    const next = [
        ...currentValues,
        value.toLowerCase()
    ]
    formik.setFieldValue(fieldName, next);
}

const _associatedVariantWatchers = (variant: VariantInterface) => {
    var productsWithAssociatedVariants = metaobject.upgrades.map((product) => product.variants.filter((newVar) => newVar.metafields?.custom?.associated_variants.includes(parseInt(variant.id)))).flat()
    productsWithAssociatedVariants = productsWithAssociatedVariants.concat(
        metaobject.accessories.map((product) => product.variants.filter((newVar) => newVar.metafields?.custom?.associated_variants.includes(parseInt(variant.id)))).flat()
    );
    return productsWithAssociatedVariants ?? [];
}

const _replaceVariantWithAssociatedVariant = (variant: VariantInterface, formik: FormikContextType<any> | FormikProps<any>) => {
    const currentValues = (formik.values.variants as ConfiguratorValue[]) ?? [];
    const watchers = _associatedVariantWatchers(variant);
    const watcherVariant = watchers.find((watcherVariant) => {
        const watcherAssociatedVariants = watcherVariant.metafields?.custom?.associated_variants.filter((variantId) => variantId != variant.id) ?? [];
        return watcherAssociatedVariants.every((variantId) =>
            currentValues.some((item: any) => item.variantId == variantId.toString()));
    });

    if (watcherVariant) {
        return watcherVariant;
    }
    return variant;
}

const _addVariants = (fieldName: string, variant: VariantInterface, formik: FormikContextType<any> | FormikProps<any>, step: number, altTitle?: string) => {
    const currentValues = (formik.values.variants as ConfiguratorValue[]) ?? [];
    let updateVariant = _replaceVariantWithAssociatedVariant(variant, formik);
    const associatedVariants = updateVariant.metafields?.custom?.associated_variants ?? [];
    // Add immutably
    const next = [
        ...currentValues,
        {
            field: fieldName,
            value: altTitle ? altTitle : updateVariant.title,
            price: updateVariant.price,
            variantId: updateVariant.id,
            image: updateVariant.featured_image.src,
            productId: updateVariant.product,
            associatedVariants: associatedVariants,
            step: step,
        } as ConfiguratorValue,
    ];
    if (associatedVariants.length > 0) {
        formik.setFieldValue('variants', [...next.filter((item: any) => !associatedVariants.some((variantId) => variantId.toString() == item.variantId))]);
        return;
    }
    formik.setFieldValue('variants', next); // new array reference
}

const addVariants = (fieldName: string, variant: VariantInterface, formik: FormikContextType<any> | FormikProps<any>, step: number, altTitle?: string) => {
    const currentValues = (formik.values.variants as ConfiguratorValue[]) ?? [];

    // If already selected, ignore
    if (currentValues.some((item: any) => item.variantId == variant.id)) return;
    _addVariants(fieldName, variant, formik, step, altTitle);
}

const updateVariants = (fieldName: string, variant: VariantInterface, formik: FormikContextType<any> | FormikProps<any>, step: number, altTitle?: string) => {
    const currentValues = (formik.values.variants as ConfiguratorValue[]) ?? [];

    // If already selected, remove and clean up auto-selected
    if (currentValues.some((item: any) => item.variantId == variant.id)) {
        const next = currentValues.filter((item: any) => item.variantId != variant.id);
        formik.setFieldValue('variants', next);  // new array reference
        return;
    }

    _addVariants(fieldName, variant, formik, step, altTitle);
}

export {
    updateModelValues,
    updateModelObjectValues,
    updateShapeValues,
    updateDimensions,
    updateIndividualDimension,
    updateVariantObject,
    updateVariants,
    updateUpsellPackages,
    updateStringArray,
    addVariants,
    changeSelectedVariant,
    clearValues,
    getShape,
    getPanels,
    getFoldDirections,
    getStrapLocations,
    getDimensionRange
}
